import { Suspense, useState, type ReactNode } from 'react';
import { Package, RefreshCw, TriangleAlert } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryClient, useQueryErrorResetBoundary } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@worldcoo/ui/components/button';
import { cn } from '@worldcoo/ui/lib/utils';

export function SectionEmptyState(props: {
	title: string;
	description: string;
	action?: ReactNode;
	className?: string;
}) {
	return (
		<div className={cn('flex flex-col items-center justify-center gap-2 px-4 text-center', props.className)}>
			<div className="flex size-12 items-center justify-center rounded-xl bg-[#ffe4d0]">
				<Package className="text-wc-web-orange-strong size-6" strokeWidth={1.5} />
			</div>
			<p className="text-foreground text-sm font-medium">{props.title}</p>
			<p className="text-muted-foreground text-xs">{props.description}</p>
			{props.action ? <div className="mt-2">{props.action}</div> : null}
		</div>
	);
}

export function SectionErrorState(props: {
	title: string;
	description?: string;
	onRetry: () => void;
	isRetrying?: boolean;
	className?: string;
}) {
	const { t } = useLingui();
	return (
		<div className={cn('flex flex-col items-center justify-center gap-2 px-4 text-center', props.className)}>
			<div className="flex size-12 items-center justify-center rounded-xl bg-[#f1f2f1]">
				<TriangleAlert className="size-6 text-[#8a8f8a]" strokeWidth={1.5} />
			</div>
			<p className="text-foreground text-sm font-medium">{props.title}</p>
			<p className="text-muted-foreground text-xs">{props.description ?? t`Try again or check back shortly.`}</p>
			<Button variant="outline-dark" onClick={props.onRetry} disabled={props.isRetrying} className="mt-3">
				<RefreshCw className={cn('size-3.5', props.isRetrying && 'animate-spin')} strokeWidth={1.8} />
				{t`Retry`}
			</Button>
		</div>
	);
}

/**
 * Lets a section load, fail and retry on its own. Every section reading a source that can be unavailable
 * needs one: a bare `<Suspense>` lets the rejection blank the page (TEC-3737). List every suspended query
 * in `retryQueryKeys`, or Retry is a plain reset.
 */
export function SectionBoundary(props: {
	fallback: ReactNode;
	errorRender: (retry: () => void, isRetrying: boolean) => ReactNode;
	retryQueryKeys?: readonly (readonly unknown[])[];
	children: ReactNode;
}) {
	const { reset } = useQueryErrorResetBoundary();
	return (
		<ErrorBoundary
			onReset={reset}
			fallbackRender={({ resetErrorBoundary }) => (
				<SectionErrorFallback
					resetErrorBoundary={resetErrorBoundary}
					retryQueryKeys={props.retryQueryKeys}
					render={props.errorRender}
				/>
			)}
		>
			<Suspense fallback={props.fallback}>{props.children}</Suspense>
		</ErrorBoundary>
	);
}

function SectionErrorFallback(props: {
	resetErrorBoundary: () => void;
	retryQueryKeys?: readonly (readonly unknown[])[];
	render: (retry: () => void, isRetrying: boolean) => ReactNode;
}) {
	const queryClient = useQueryClient();
	const [isRetrying, setIsRetrying] = useState(false);

	const retry = async () => {
		const keys = props.retryQueryKeys ?? [];
		// Only the failed ones: an entry with no observer has no `queryFn`, and refetching it errors.
		const failed = keys.filter((queryKey) => queryClient.getQueryState(queryKey)?.status === 'error');
		if (failed.length === 0) {
			props.resetErrorBoundary();
			return;
		}
		setIsRetrying(true);
		await Promise.all(failed.map((queryKey) => queryClient.refetchQueries({ queryKey })));
		// Resetting now would loop through the skeleton on useSuspenseQuery's re-throw.
		const stillFailing = failed.some((queryKey) => queryClient.getQueryState(queryKey)?.status === 'error');
		setIsRetrying(false);
		if (!stillFailing) {
			props.resetErrorBoundary();
		}
	};

	return <>{props.render(retry, isRetrying)}</>;
}
